import React, { useState } from "react";
import { Icon } from "@fluentui/react";
import { GroupedChatContext } from "../hooks/useChatContextTracker";

interface AttachedContextDisplayProps {
  trackedContext: GroupedChatContext;
  onClose?: () => void;
}

const AttachedContextDisplay: React.FC<AttachedContextDisplayProps> = ({
  trackedContext,
  onClose
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (trackedContext.contexts.length === 0) {
    return null;
  }

  const uniqueEndpoints = Array.from(trackedContext.uniqueEndpointsByFullEndpoint.values());
  const totalEndpoints = uniqueEndpoints.length;
  const descriptions = uniqueEndpoints
    .map(endpoint => endpoint.dataExtractor.description)
    .filter(desc => desc && desc.trim() !== "");

  return (
    <div style={{
      margin: "8px 0",
      padding: "8px 12px",
      backgroundColor: "#f3f2f1",
      border: "1px solid #e1dfdd",
      borderRadius: "8px",
      fontSize: "12px"
    }}>
      {/* Header with toggle button */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer"
      }}
      onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Icon iconName="History" style={{ fontSize: "12px", color: "#605e5c" }} />
          <span style={{ color: "#323130", fontWeight: 500 }}>
            {totalEndpoints} attached datasets found in previous messages
          </span>
          <Icon
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            style={{ fontSize: "10px", color: "#605e5c" }}
          />
        </div>
        {onClose && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "2px",
              color: "#605e5c",
              borderRadius: "2px"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e1dfdd";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <Icon iconName="Cancel" style={{ fontSize: "10px" }} />
          </button>
        )}
      </div>

      {/* Expanded content showing endpoint descriptions */}
      {isExpanded && (
        <div style={{
          marginTop: "8px",
          padding: "8px",
          backgroundColor: "#ffffff",
          border: "1px solid #e1dfdd",
          borderRadius: "4px"
        }}>
          {descriptions.length > 0 ? (
            <div style={{ color: "#323130", lineHeight: "1.4" }}>
              {descriptions.map((desc, index) => (
                <div key={index} style={{ marginBottom: "4px" }}>
                  <span style={{ color: "#605e5c", fontSize: "11px" }}>
                    {index + 1}.
                  </span>{" "}
                  <span style={{ fontSize: "11px" }}>
                    {desc}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              color: "#605e5c",
              fontSize: "11px",
              fontStyle: "italic"
            }}>
              No endpoint descriptions available
            </div>
          )}

          {/* Summary of contexts found */}
          <div style={{
            marginTop: "8px",
            paddingTop: "6px",
            borderTop: "1px solid #e1dfdd",
            fontSize: "11px",
            color: "#605e5c"
          }}>
            Found in {trackedContext.contexts.length} message{trackedContext.contexts.length === 1 ? "" : "s"}
            {trackedContext.contexts.length > 0 && (
              <span>
                {" "}(messages #{trackedContext.contexts.map(c => c.messageIndex + 1).join(", ")})
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachedContextDisplay;